import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, KeyRound } from 'lucide-react';
import { SafeSection } from '../components/SafeTickets';
import BlowoutSpreads from '../components/BlowoutSpreads';
import { GameCard } from '../components/SuggestionsTab';
import CfbGameCard from '../components/CfbGameCard';
import PicksLegend from '../components/PicksLegend';
import WhoWins from '../components/WhoWins';
import BetModeSwitch from '../components/BetModeSwitch';
import { ensureBetMode } from '../lib/betMode';
import GuestTracker from '../components/GuestTracker';

const CodeGate = ({ onSubmit, error, busy }) => {
  const [code, setCode] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(code); }} className="max-w-sm mx-auto mt-10 space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/70 p-5" data-testid="public-code-gate">
      <p className="text-sm font-semibold text-white flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-400" />Enter today's access code</p>
      <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoFocus placeholder="6-digit code"
        className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-center font-mono text-2xl tracking-[0.4em] text-white" data-testid="public-code-input" />
      {error && <p className="text-xs text-red-400" data-testid="public-code-error">{error}</p>}
      <button type="submit" disabled={code.length !== 6 || busy} data-testid="public-code-submit"
        className="w-full rounded-lg bg-amber-500 py-2.5 text-sm font-bold text-black hover:bg-amber-400 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'View picks'}
      </button>
      <p className="text-[11px] text-zinc-500">Each code works for 7 days.</p>
    </form>
  );
};

const Games = ({ data }) => {
  const [tab, setTab] = useState('nfl');
  const games = data[tab].games || [];
  return (
    <div className="space-y-2" data-testid="public-games">
      <div className="flex gap-2">
        {[['nfl', `NFL Week ${data.nfl.week || ''}`], ['cfb', `College Week ${data.cfb.week || ''}`], ['nba', 'NBA']].filter(([k]) => data[k]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`public-tab-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${tab === k ? 'bg-amber-500 text-black border-amber-500' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>{label} ({data[k].games.length})</button>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500">Tap a game to see what each team should do.</p>
      {games.map((g) => (tab === 'cfb' ? <CfbGameCard key={g.event_id} g={g} /> : <GameCard key={g.event_id} g={g} />))}
    </div>
  );
};

// Public, code-gated, read-only: Nick's Picks picks only. No app navigation.
export default function PublicPicksPage() {
  const { token } = useParams();
  useState(() => ensureBetMode('pro')); // visitors see pro wording unless they switch
  const store = `nicks_code_${token}`;
  const [data, setData] = useState(null);
  const [state, setState] = useState({ busy: false, error: null, gate: true, dead: null });
  const load = useCallback(async (code) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      // No code sent = owner check: a PIN-signed-in owner gets straight in; everyone else sees the code screen.
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/picks/${token}`, { headers: code ? { 'X-Access-Code': code } : {}, withCredentials: true });
      if (code) sessionStorage.setItem(store, code);
      setData(r.data); setState({ busy: false, error: null, gate: false, dead: null });
    } catch (e) {
      sessionStorage.removeItem(store);
      const st = e.response?.status; const msg = e.response?.data?.detail || 'Could not load picks';
      setData(null);
      setState({ busy: false, gate: st === 401 || st === 429, error: st === 401 && code ? 'Wrong or expired code' : st === 429 ? msg : null, dead: st === 404 ? msg : null });
    }
  }, [token, store]);
  useEffect(() => { document.title = "Nick's Picks"; load(sessionStorage.getItem(store) || ''); }, [load, store]);
  useEffect(() => {
    if (!data?.valid_until) return undefined;
    const ms = new Date(data.valid_until).getTime() - Date.now();
    const t = setTimeout(() => { sessionStorage.removeItem(store); setData(null); setState({ busy: false, gate: true, error: 'Code expired — ask for a new code', dead: null }); }, Math.max(ms, 0));
    return () => clearTimeout(t);
  }, [data, store]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-8" data-testid="public-picks-page">
      <div className="max-w-4xl mx-auto space-y-5">
        <img src="/nicks-picks-og-v2.jpg" alt="Nick's Picks — Weekly Best Bets" className="w-full rounded-2xl border border-zinc-800 shadow-2xl" data-testid="public-picks-banner" />
        <h1 className="sr-only" data-testid="public-picks-title">Nick's Picks</h1>
        <div className="flex justify-end"><BetModeSwitch /></div>
        {state.dead && <p className="text-sm text-red-400" data-testid="public-picks-error">{state.dead}</p>}
        {!state.dead && !data && state.gate && <CodeGate onSubmit={load} error={state.error} busy={state.busy} />}
        {data && (
          <>
            <GuestTracker token={token} code={sessionStorage.getItem(store) || ''} />
            <PicksLegend />
            <p className="text-xs text-zinc-500">Tap a ticket to see its legs, then "Why it's safe" for the stats. Access ends at midnight Eastern.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SafeSection title={`College Football Week ${data.cfb.week || ''}`} tickets={data.cfb.tickets} league="cfb" trackable={false}>
                <BlowoutSpreads spreads={data.cfb.spreads} />
              </SafeSection>
              <SafeSection title={`NFL Week ${data.nfl.week || ''} (Thu–Mon)`} tickets={data.nfl.tickets} league="nfl" trackable={false} />
            </div>
            {data.nba && (
              <div className="space-y-3" data-testid="public-nba">
                <SafeSection title="NBA nightly safe tickets" tickets={data.nba.tickets} league="nba" trackable={false} />
              </div>
            )}
            <WhoWins data={data} />
            <Games data={data} />
          </>
        )}
        <p className="text-[10px] text-zinc-600">For entertainment only. Picks are model estimates, not guarantees.</p>
      </div>
    </div>
  );
}
